/* eslint-disable @typescript-eslint/consistent-type-assertions */

import {
    AccountResource,
    AccountType,
    CertificateResource,
    KubernetesAws,
    KubernetesAwsAuthentication,
    KubernetesAzure,
    KubernetesAzureAuthentication,
    KubernetesCertificate,
    KubernetesCertificateAuthentication,
    KubernetesStandard,
    KubernetesStandardAccountAuthentication,
    Permission,
    ProxyResource,
    WorkerPoolResource,
} from "../../../../../client/resources";
import * as React from "react";
import { DoBusyTask } from "../../../../../components/DataBaseComponent/DataBaseComponent";
import { KubernetesEndpointResource } from "../../../../../client/resources/endpointResource";
import CommonSummaryHelper from "../../../../../utils/CommonSummaryHelper";
import AccountSelect from "../../../../../components/form/AccountSelect/AccountSelect";
import ExpandableFormSection from "../../../../../components/form/Sections/ExpandableFormSection";
import Note from "../../../../../components/form/Note/Note";
import StringCheckbox from "../../../../../components/form/Checkbox/StringCheckbox";
import { Text } from "components/form";
import RadioButton from "components/form/RadioButton/RadioButton";
import Summary from "components/form/Sections/Summary";
import Select from "components/form/Select/Select";
import CertificateSelect from "components/form/CertificateSelect/CertificateSelect";
import RadioButtonGroup from "components/form/RadioButton/RadioButtonGroup";
import { repository } from "clientInstance";
import { CategorizedEndpointRegistration, CommunicationStyle, EndpointRegistrationKey } from "./endpointRegistry";
import EndpointCard from "./EndpointCard";
import ExternalLink from "components/Navigation/ExternalLink";
import { KubernetesCluster } from "components/Images/Images/MachineSettings/KubernetesCluster";
import { ExecutionContainerImageSelector, StepExecutionOption } from "../../../../../components/ContainerSelector";
import FeedResource from "../../../../../client/resources/feedResource";
import { EarlyAccessChip } from "../../../../../components/Chips";
import { isAllowed } from "../../../../../components/PermissionCheck/PermissionCheck";
import configurationSelectors from "../../../../configuration/reducers/selectors";
import { connect } from "react-redux";
// TODO: Cleanup - We should seriously reconsider importing from other areas like this.
import { RunOnDeploymentTarget, RunOnServerOrWorkerPool, RunOnWorkerPool, RunOn, ExecutionLocation, RunOnBuiltInWorker } from "areas/projects/components/Process/types";
import { generateDefaultActionContainer, isRunOnBuiltInWorker, isRunOnServerOrWorkerPool } from "areas/projects/components/Process/Common/CommonProcessHelpers";
import _ from "lodash";
const styles = require("./styles.less");

interface KubernetesEndpointProps {
    proxies: ProxyResource[];
    doBusyTask: DoBusyTask;
    busy: Promise<void> | boolean;
    endpoint: KubernetesEndpointResource;
    accounts: AccountResource[];
    workerPools: WorkerPoolResource[];
    certificates: () => Promise<CertificateResource[]>;
    refreshAccounts: () => Promise<{}>;
    refreshCertificates: () => Promise<boolean>;
    isBuiltInWorkerEnabled: boolean;
    feeds: FeedResource[];
    imageNameError?: string;
    refreshFeeds: (callback?: (feeds: FeedResource[]) => void) => Promise<void>;
    onChange(newValue: KubernetesEndpointResource): void;
    getFieldError(field: string): string;
}

interface KubernetesEndpointState {
    certificate: CertificateResource | undefined;
    account: AccountResource | undefined;
    executionContainerSelected: boolean;
}

export class KubernetesEndpoint extends React.Component<KubernetesEndpointProps, KubernetesEndpointState> {
    constructor(props: KubernetesEndpointProps) {
        super(props);
        this.state = {
            certificate: undefined,
            account: undefined,
            executionContainerSelected: !!props.endpoint.Container?.Image && !!props.endpoint.Container?.FeedId,
        };
    }

    async componentDidMount() {
        await this.props.doBusyTask(async () => {
            await this.getCertificateResource();
            await this.getAccountResource();
        });
    }

    render() {
        return (
            <div>
                <ExpandableFormSection errorKey="Account" title="Authentication" focusOnExpandAll summary={this.accountSummary()} help="Select the account or certificate that identifies the Kubernetes user.">
                    <Note>You can choose to authenticate with the Kubernetes cluster with a username and password, AWS account, Azure account, token or client certificate.</Note>
                    <RadioButtonGroup
                        value={this.getAccountType()}
                        onChange={(x: string) => {
                            const endpoint = this.props.endpoint;
                            // If switching types, clear any previous selection
                            if (endpoint.AccountType !== x) {
                                endpoint.Authentication = null;
                            }
                            endpoint.AccountType = x;
                            this.props.onChange(endpoint);
                        }}
                    >
                        <RadioButton value={AccountType.UsernamePassword} label="Username and password" />
                        <RadioButton value={AccountType.Token} label="Token" />
                        <RadioButton value={AccountType.AzureServicePrincipal} label="Azure Service Principal" />
                        <RadioButton value={AccountType.AmazonWebServicesAccount} label="AWS Account" />
                        <RadioButton value={KubernetesCertificate} label="Client Certificate" />
                    </RadioButtonGroup>
                    {this.getAccountType() === AccountType.UsernamePassword && (
                        <AccountSelect
                            onRequestRefresh={this.props.refreshAccounts}
                            value={this.getStandardAuth().AccountId}
                            type={[AccountType.UsernamePassword]}
                            allowClear={true}
                            onChange={async x => {
                                const endpoint = this.props.endpoint;
                                this.getStandardAuth().AccountId = x;
                                endpoint.AccountType = AccountType.UsernamePassword;
                                this.props.onChange(endpoint);
                                await this.getAccountResource();
                            }}
                            items={this.props.accounts}
                        />
                    )}
                    {this.getAccountType() === AccountType.Token && (
                        <AccountSelect
                            onRequestRefresh={this.props.refreshAccounts}
                            value={this.getStandardAuth().AccountId}
                            type={[AccountType.Token]}
                            allowClear={true}
                            onChange={async x => {
                                const endpoint = this.props.endpoint;
                                this.getStandardAuth().AccountId = x;
                                endpoint.AccountType = AccountType.Token;
                                this.props.onChange(endpoint);
                                await this.getAccountResource();
                            }}
                            items={this.props.accounts}
                        />
                    )}
                    {this.getAccountType() === AccountType.AzureServicePrincipal && (
                        <div>
                            <AccountSelect
                                onRequestRefresh={this.props.refreshAccounts}
                                value={this.getAzureAuth().AccountId}
                                type={[AccountType.AzureServicePrincipal]}
                                allowClear={true}
                                onChange={async x => {
                                    const endpoint = this.props.endpoint;
                                    this.getAzureAuth().AccountId = x;
                                    endpoint.AccountType = AccountType.AzureServicePrincipal;
                                    this.props.onChange(endpoint);
                                    await this.getAccountResource();
                                }}
                                items={this.props.accounts}
                            />
                            <Text
                                value={this.getAzureAuth().ClusterName}
                                onChange={x => {
                                    const endpoint = this.props.endpoint;
                                    this.getAzureAuth().ClusterName = x;
                                    this.props.onChange(endpoint);
                                }}
                                error={this.props.getFieldError("ClusterName")}
                                label="AKS cluster name"
                            />
                            <Text
                                value={this.getAzureAuth().ClusterResourceGroup}
                                onChange={x => {
                                    const endpoint = this.props.endpoint;
                                    this.getAzureAuth().ClusterResourceGroup = x;
                                    this.props.onChange(endpoint);
                                }}
                                error={this.props.getFieldError("ClusterResourceGroup")}
                                label="AKS resource group name"
                            />
                        </div>
                    )}
                    {this.getAccountType() === AccountType.AmazonWebServicesAccount && (
                        <div>
                            <RadioButtonGroup
                                value={!!this.getAwsAuth().UseInstanceRole}
                                onChange={x => {
                                    const endpoint = this.props.endpoint;
                                    this.getAwsAuth().UseInstanceRole = x;
                                    this.getAwsAuth().AccountId = "";
                                    this.props.onChange(endpoint);
                                }}
                                label="Execute using the AWS service role for an EC2 instance"
                            >
                                <RadioButton value={true} label="Yes" />
                                <RadioButton value={false} label="No" />
                            </RadioButtonGroup>
                            {!this.getAwsAuth().UseInstanceRole && (
                                <AccountSelect
                                    onRequestRefresh={this.props.refreshAccounts}
                                    value={this.getAwsAuth().AccountId}
                                    type={[AccountType.AmazonWebServicesAccount]}
                                    allowClear={true}
                                    onChange={async x => {
                                        const endpoint = this.props.endpoint;
                                        this.getAwsAuth().AccountId = x;
                                        endpoint.AccountType = AccountType.AmazonWebServicesAccount;
                                        this.props.onChange(endpoint);
                                        await this.getAccountResource();
                                    }}
                                    items={this.props.accounts}
                                />
                            )}
                            <RadioButtonGroup
                                value={!!this.getAwsAuth().AssumeRole}
                                onChange={x => {
                                    const endpoint = this.props.endpoint;
                                    this.getAwsAuth().AssumeRole = x;
                                    this.props.onChange(endpoint);
                                }}
                                label="Assume a different AWS service role"
                            >
                                <RadioButton value={true} label="Yes" />
                                <RadioButton value={false} label="No" />
                            </RadioButtonGroup>
                            {!!this.getAwsAuth().AssumeRole && (
                                <div>
                                    <Text
                                        label="Assumed role ARN"
                                        value={this.getAwsAuth().AssumedRoleArn || ""}
                                        onChange={x => {
                                            const endpoint = this.props.endpoint;
                                            this.getAwsAuth().AssumedRoleArn = x;
                                            this.props.onChange(endpoint);
                                        }}
                                        error={this.props.getFieldError("AssumedRoleArn")}
                                    />
                                    <Text
                                        label="Assumed role session name"
                                        value={this.getAwsAuth().AssumedRoleSession || ""}
                                        onChange={x => {
                                            const endpoint = this.props.endpoint;
                                            this.getAwsAuth().AssumedRoleSession = x;
                                            this.props.onChange(endpoint);
                                        }}
                                        error={this.props.getFieldError("AssumedRoleSession")}
                                    />
                                    <Text
                                        label="Assumed role session duration (in seconds)"
                                        type="number"
                                        min={900}
                                        max={43200}
                                        value={_.toString(this.getAwsAuth().AssumeRoleSessionDurationSeconds)}
                                        onChange={x => {
                                            const endpoint = this.props.endpoint;
                                            this.getAwsAuth().AssumeRoleSessionDurationSeconds = _.toNumber(x) || null;
                                            this.props.onChange(endpoint);
                                        }}
                                        error={this.props.getFieldError("AssumeRoleSessionDurationSeconds")}
                                    />
                                    <Note>If blank, defaults to 3600 seconds (1 hour).</Note>
                                    <Text
                                        label="Assumed role external ID"
                                        value={this.getAwsAuth().AssumeRoleExternalId || ""}
                                        onChange={x => {
                                            const endpoint = this.props.endpoint;
                                            this.getAwsAuth().AssumeRoleExternalId = x;
                                            this.props.onChange(endpoint);
                                        }}
                                        error={this.props.getFieldError("AssumeRoleExternalId")}
                                    />
                                    <Note>
                                        {" "}
                                        Learn more about <ExternalLink href="AwsDocsRolesTermsAndConcepts">Roles Terms and Concepts</ExternalLink>.
                                    </Note>
                                </div>
                            )}
                            <Text
                                value={this.getAwsAuth().ClusterName}
                                onChange={x => {
                                    const endpoint = this.props.endpoint;
                                    this.getAwsAuth().ClusterName = x;
                                    this.props.onChange(endpoint);
                                }}
                                error={this.props.getFieldError("ClusterName")}
                                label="EKS cluster name"
                            />
                        </div>
                    )}
                    {this.getAccountType() === KubernetesCertificate && (
                        <div>
                            <CertificateSelect
                                allowClear={true}
                                value={this.getCertificateAuth().ClientCertificate}
                                error={this.props.getFieldError("ClientCertificate")}
                                onChange={async x => {
                                    const endpoint = this.props.endpoint;
                                    this.getCertificateAuth().ClientCertificate = x;
                                    endpoint.AccountType = KubernetesCertificate;
                                    this.props.onChange(endpoint);
                                    await this.getCertificateResource();
                                }}
                                items={this.props.certificates}
                                onRequestRefresh={this.props.refreshCertificates}
                                doBusyTask={this.props.doBusyTask}
                            />
                            <Note>The certificate that identifies the user.</Note>
                        </div>
                    )}
                </ExpandableFormSection>
                <ExpandableFormSection errorKey={"ClusterURL"} title="Kubernetes Details" summary={this.kubernetesSummary()} help={"Enter the Kubernetes cluster details."}>
                    {this.getAccountType() !== AccountType.AzureServicePrincipal && (
                        <div>
                            <Text
                                value={this.props.endpoint.ClusterUrl}
                                onChange={x => {
                                    const endpoint = this.props.endpoint;
                                    endpoint.ClusterUrl = x;
                                    this.props.onChange(endpoint);
                                }}
                                error={this.props.getFieldError("ClusterURL")}
                                label="Kubernetes cluster URL"
                            />
                            <Note>
                                Must be an absolute URL. e.g. <em>https://kubernetes.example.com</em>
                            </Note>
                            <CertificateSelect
                                allowClear={true}
                                value={this.props.endpoint.ClusterCertificate}
                                error={this.props.getFieldError("ClusterCertificate")}
                                onChange={x => {
                                    const endpoint = this.props.endpoint;
                                    endpoint.ClusterCertificate = x;
                                    this.props.onChange(endpoint);
                                }}
                                items={this.props.certificates}
                                onRequestRefresh={this.props.refreshCertificates}
                                doBusyTask={this.props.doBusyTask}
                            />
                            <Note>The optional cluster certificate authority.</Note>
                            {!this.props.endpoint.ClusterCertificate && (
                                <div>
                                    <StringCheckbox
                                        value={this.props.endpoint.SkipTlsVerification}
                                        onChange={x => {
                                            const endpoint = this.props.endpoint;
                                            endpoint.SkipTlsVerification = x;
                                            this.props.onChange(endpoint);
                                        }}
                                        label="Skip TLS verification"
                                    />
                                    <Note>Enable this option to skip the verification of the cluster certificate. This can only be selected if no cluster certificate is specified.</Note>
                                </div>
                            )}
                        </div>
                    )}
                    <Text
                        value={this.props.endpoint.Namespace}
                        onChange={x => {
                            const endpoint = this.props.endpoint;
                            endpoint.Namespace = x;
                            this.props.onChange(endpoint);
                        }}
                        placeholder="default"
                        error={this.props.getFieldError("Namespace")}
                        label="Kubernetes namespace"
                    />
                </ExpandableFormSection>
                {this.props.workerPools.length > 1 && (
                    <ExpandableFormSection
                        errorKey={"DefaultWorkerPool"}
                        title="Worker Pool"
                        summary={this.props.endpoint.DefaultWorkerPoolId ? CommonSummaryHelper.resourceSummary(this.props.endpoint.DefaultWorkerPoolId, this.props.workerPools, "worker pool") : Summary.placeholder("No pool selected - default pool")}
                        help="Select the optional worker pool."
                    >
                        <Select
                            label={"Select a default pool"}
                            items={this.props.workerPools.map(e => ({ value: e.Id, text: e.Name }))}
                            value={this.props.endpoint.DefaultWorkerPoolId}
                            allowFilter={true}
                            allowClear={true}
                            onChange={x => {
                                this.props.endpoint.DefaultWorkerPoolId = x;
                                this.props.onChange(this.props.endpoint);
                            }}
                        />
                    </ExpandableFormSection>
                )}
                {this.renderContainerImageSelector()}
            </div>
        );
    }

    private renderContainerImageSelector() {
        const runOn = this.whereToRun();

        if (!isRunOnServerOrWorkerPool(runOn)) {
            return null;
        }

        const feedViewPermissionGranted = isAllowed({ permission: Permission.FeedView, wildcard: true });

        return (
            <ExpandableFormSection
                errorKey="Octopus.Action.Container.Image|Octopus.Action.Container.FeedId"
                title="Health Check Container Image"
                summary={CommonSummaryHelper.actionContainerSummary(runOn.container, this.props.feeds || [], runOn, !feedViewPermissionGranted)}
                help={`Choose to run directly on ${isRunOnBuiltInWorker(runOn) ? "the Octopus Server" : "a worker"} or inside a container`}
            >
                {this.state.executionContainerSelected && <Note>This image is only used when performing health checks. It is not used when performing deployments.</Note>}
                <ExecutionContainerImageSelector
                    runOn={runOn}
                    feeds={this.props.feeds}
                    workerPoolId={this.props.endpoint.DefaultWorkerPoolId}
                    refreshFeeds={this.props.refreshFeeds}
                    onFeedIdChange={feedId => this.onFeedIdChanged(feedId, runOn)}
                    onImageNameChange={imageName => this.onImageNameChanged(imageName, runOn)}
                    resetContainer={updatedRunOn => this.onContainerReset(updatedRunOn)}
                    onStepExecutionOptionChange={option => {
                        if (option === StepExecutionOption.RunInsideContainerOnWorker) {
                            this.onRunOnChanged({ ...runOn, runningInContainer: true });
                        } else {
                            this.onRunOnChanged({ ...runOn, runningInContainer: false, container: generateDefaultActionContainer() });
                        }
                    }}
                    imageNameError={this.props.imageNameError}
                />
            </ExpandableFormSection>
        );
    }
    private onFeedIdChanged = (feedId: string, runOn: RunOnServerOrWorkerPool) => {
        const updatedContainer = { ...runOn.container, FeedId: feedId };
        this.onRunOnChanged({ runningInContainer: runOn.runningInContainer, executionLocation: runOn.executionLocation, container: updatedContainer });
    };

    private onImageNameChanged = (imageName: string, runOn: RunOnServerOrWorkerPool) => {
        const updatedContainer = { ...runOn.container, Image: imageName };
        this.onRunOnChanged({ runningInContainer: runOn.runningInContainer, executionLocation: runOn.executionLocation, container: updatedContainer });
    };

    private onContainerReset = (runOn: RunOnServerOrWorkerPool) => {
        this.onRunOnChanged({ ...runOn });
    };

    private onRunOnChanged = (runOn: RunOnBuiltInWorker | RunOnWorkerPool) => {
        if (runOn.runningInContainer) {
            this.setState({ executionContainerSelected: runOn.runningInContainer });
            if (this.props.endpoint.Container?.FeedId !== runOn.container.FeedId || this.props.endpoint.Container?.Image !== runOn.container.Image) {
                this.props.endpoint.Container = { ...runOn.container };
                this.props.onChange(this.props.endpoint);
            }
        } else {
            if (this.props.endpoint.Container !== null) {
                this.props.endpoint.Container = null;
                this.props.onChange(this.props.endpoint);
            }
        }
    };

    private getAccountType() {
        // First use any value saved in the AccountType field
        if (this.props.endpoint.AccountType) {
            return this.props.endpoint.AccountType;
        }

        // Inspect the account resource
        if (this.state.account) {
            return this.state.account.AccountType;
        }

        // Inspect the certificate resource
        if (this.state.certificate) {
            return KubernetesCertificate;
        }

        // Using an instance role means we have an AWS account
        if ((this.props.endpoint.Authentication as KubernetesAwsAuthentication)?.UseInstanceRole) {
            return AccountType.AmazonWebServicesAccount;
        }

        return null;
    }

    private accountSummary() {
        if (this.props.endpoint.Authentication) {
            if (this.props.endpoint.Authentication.AuthenticationType === KubernetesCertificate) {
                if (this.state.certificate) {
                    return Summary.summary(this.state.certificate.Name);
                }
                return Summary.summary(<span>Using certificate</span>);
            }

            const stdAccount = this.props.endpoint.Authentication as KubernetesStandardAccountAuthentication;
            if (stdAccount.AccountId) {
                return CommonSummaryHelper.resourceSummary(stdAccount.AccountId, this.props.accounts, "account");
            }

            const awsAccount = this.props.endpoint.Authentication as KubernetesAwsAuthentication;
            if (awsAccount?.UseInstanceRole) {
                return Summary.summary(<span>Using AWS instance role</span>);
            }
        }

        return Summary.placeholder("No account or certificate selected");
    }

    private kubernetesSummary() {
        if (this.props.endpoint.ClusterUrl) {
            const tlsNotes = this.props.endpoint.ClusterCertificate ? " with a custom certificate" : this.props.endpoint.SkipTlsVerification === "True" ? " without TLS verification" : " with TLS verification";

            const namespaceNotes = this.props.endpoint.Namespace ? (
                <span>
                    {" "}
                    and namespace <strong>{this.props.endpoint.Namespace}</strong>
                </span>
            ) : (
                <span />
            );

            return Summary.summary(
                <span>
                    Connecting to cluster at <strong>{this.props.endpoint.ClusterUrl}</strong>
                    {namespaceNotes}
                    {tlsNotes}
                </span>
            );
        }

        if (this.props.endpoint.Namespace) {
            return Summary.summary(
                <span>
                    Namespace is <strong>{this.props.endpoint.Namespace}</strong>
                </span>
            );
        }

        return Summary.placeholder("No cluster URL or namespace");
    }

    private async getCertificateResource() {
        if (this.props.endpoint.Authentication && this.props.endpoint.Authentication.AuthenticationType === KubernetesCertificate) {
            const auth = this.props.endpoint.Authentication as KubernetesCertificateAuthentication;
            if (auth.ClientCertificate) {
                const certificate = await repository.Certificates.get(auth.ClientCertificate);
                this.setState({
                    certificate,
                    // Clear the account if we have an account selected
                    account: !certificate ? this.state.account : undefined,
                });
            }
        }
    }

    private async getAccountResource() {
        if (this.props.endpoint.Authentication && this.props.endpoint.Authentication.AuthenticationType !== KubernetesCertificate) {
            const auth = this.props.endpoint.Authentication as KubernetesStandardAccountAuthentication;
            if (auth.AccountId) {
                const account = await repository.Accounts.get((this.props.endpoint.Authentication as KubernetesStandardAccountAuthentication).AccountId);
                this.setState({
                    account,
                    // Clear the certificate if we have an account selected
                    certificate: !account ? this.state.certificate : undefined,
                });
            }
        }
    }

    /**
     * Return the Aws authentication object, creating a new one if necessary
     */
    private getAwsAuth(): KubernetesAwsAuthentication {
        if (!this.props.endpoint.Authentication || this.props.endpoint.Authentication.AuthenticationType !== KubernetesAws) {
            this.props.endpoint.Authentication = new KubernetesAwsAuthentication();
        }

        return this.props.endpoint.Authentication as KubernetesAwsAuthentication;
    }

    /**
     * Return the Azure authentication object, creating a new one if necessary
     */
    private getAzureAuth(): KubernetesAzureAuthentication {
        if (!this.props.endpoint.Authentication || this.props.endpoint.Authentication.AuthenticationType !== KubernetesAzure) {
            this.props.endpoint.Authentication = new KubernetesAzureAuthentication();
            // Azure supplies these values for us
            this.props.endpoint.ClusterUrl = "";
            this.props.endpoint.ClusterCertificate = "";
            this.props.endpoint.SkipTlsVerification = "False";
        }

        return this.props.endpoint.Authentication as KubernetesAzureAuthentication;
    }

    /**
     * Return the standard authentication object, creating a new one if necessary
     */
    private getStandardAuth(): KubernetesStandardAccountAuthentication {
        if (!this.props.endpoint.Authentication || this.props.endpoint.Authentication.AuthenticationType !== KubernetesStandard) {
            this.props.endpoint.Authentication = new KubernetesStandardAccountAuthentication();
        }

        return this.props.endpoint.Authentication as KubernetesStandardAccountAuthentication;
    }

    /**
     * Return the Certificate authentication object, creating a new one if necessary
     */
    private getCertificateAuth(): KubernetesCertificateAuthentication {
        if (!this.props.endpoint.Authentication || this.props.endpoint.Authentication.AuthenticationType !== KubernetesCertificate) {
            this.props.endpoint.Authentication = new KubernetesCertificateAuthentication();
        }

        return this.props.endpoint.Authentication as KubernetesCertificateAuthentication;
    }

    private whereToRun(): RunOn {
        if (this.props.workerPools.length > 0) {
            const runOn = new RunOnWorkerPool();
            runOn.container = this.props.endpoint.Container || generateDefaultActionContainer();
            runOn.executionLocation = ExecutionLocation.WorkerPool;
            runOn.runningInContainer = this.state.executionContainerSelected;
            return runOn;
        } else if (this.props.isBuiltInWorkerEnabled) {
            const runOn = new RunOnBuiltInWorker();
            runOn.container = this.props.endpoint.Container || generateDefaultActionContainer();
            runOn.executionLocation = ExecutionLocation.OctopusServer;
            runOn.runningInContainer = this.state.executionContainerSelected;
            return runOn;
        }

        return new RunOnDeploymentTarget();
    }
}

export default KubernetesEndpoint;

const kubernetesEndpointRegistration: CategorizedEndpointRegistration = {
    key: EndpointRegistrationKey.Kubernetes,
    displayOrder: 20,
    communicationStyle: CommunicationStyle.Kubernetes,
    name: "Kubernetes Cluster",
    categories: [
        {
            category: "Kubernetes Cluster",
            title: (
                <React.Fragment>
                    Let's setup a <strong>Kubernetes Cluster</strong> target.
                </React.Fragment>
            ),
            help: (
                <Note>
                    Learn more about <ExternalLink href="KubernetesTarget">Kubernetes Targets</ExternalLink>
                </Note>
            ),
            displayOrder: 30,
        },
    ],
    renderCard: ({ registration, category, getNavigationProps }) => (
        <EndpointCard
            logo={<KubernetesCluster className={styles.centreThumbnail} width="4.5rem" title={registration.name} />}
            header={registration.name}
            description="Connect to an existing Kubernetes Cluster (K8S account and namespace boundary)."
            {...getNavigationProps()}
        />
    ),
};

export { kubernetesEndpointRegistration };
